/**
 * sync.ts — amplop batch sinkronisasi, mesin status ack, dan idempotensi.
 *
 * Sumber kebenaran: `design/SCHEMA-v0.md` §1 · §2 · §3 · §10 · §10b ·
 * `research/SYNC-ARCHITECTURE.md` §1–§5 · `research/ARCHITECTURE-client.md` §5–§6b ·
 * Guardrail P1–P10 (CLAUDE.md §Guardrail).
 *
 * ### Cakupan
 * Bentuk tipe amplop naik/turun · `SyncState` + mesin transisi · hasil ack per-record ·
 * ringkasan batch · bentuk data retry/health · cara peristiwa `events.ts` menumpang amplop.
 *
 * ### BUKAN di sini (tegas)
 * Klien HTTP · transport · retry loop · autentikasi (M2M **OAuth2 client-credentials PS256**
 * sudah jadi keputusan §10b — **jangan buat mekanisme kedua**) · kompresi · DDL · mesin hitung
 * pajak. Berkas ini mendefinisikan **bentuk** dan **aturan**, bukan cara mengirimnya.
 *
 * ### Ketergantungan internal
 * - `money.ts` — **tipe saja**. Nilai `MONEY`/`QTY` di dalam payload adalah **string desimal
 *   kanonik** (`MoneyString`/`QtyString`), tak pernah `number` (P1). Berkas ini menegakkannya
 *   secara struktural lewat {@link scanWirePrecisionViolation}.
 * - `events.ts` — tipe `AnyEvent` (dialiaskan {@link OutboxEvent}), kunci natural, dan guard
 *   `TS`/`BDATE`/`CODE`/UUIDv7. ⚠️ `sync.ts` **tidak** mendefinisikan ulang nama peristiwa;
 *   kalau ia mendefinisikan ulang, tiga lapis akan menyimpang — itu justru alasan paket ini ada.
 *
 * ### Konvensi wire
 * Nama field JSON = `snake_case`, **identik** dengan nama kolom di SCHEMA-v0. Interface TS memakai
 * key `snake_case` yang sama ⇒ serialisasi = identitas, tak ada lapisan pemetaan yang bisa
 * menyimpang diam-diam. Nama *tipe* tetap Inggris PascalCase.
 *
 * ### Bergantung pada keputusan yang masih 🔶 terbuka
 * Angka retensi & jendela idempotensi terikat 🔶#4 (*berapa lama terminal boleh jualan tanpa
 * server*); `lease_token` terikat 🔶#2 (*wasit lease saat portal offline*). ⇒ kontrak mewajibkan
 * **bentuk field**-nya ada, dan **melarang angkanya jadi konstanta terpahat** (setelan, sejalan P4).
 *
 * ### Catatan penyimpangan dari spesifikasi
 * - Spesifikasi menamai hasil parse `ParseResult<T>`. Nama itu **sudah dipakai `events.ts`** dengan
 *   bentuk berbeda (`{ errors: ParseError[] }`), dan `tax.ts` sudah memilih jalan yang sama untuk
 *   kasus ini (`TaxParseResult`). Di sini namanya {@link SyncParseResult} agar barrel `index.ts`
 *   tak bertabrakan. Bentuk isinya persis seperti spesifikasi.
 * - Spesifikasi menyebut tipe `OutboxEvent` "yang didefinisikan di `events.ts`". Yang ada di sana
 *   bernama `AnyEvent`; {@link OutboxEvent} di berkas ini **alias murni** ke tipe itu — bukan
 *   definisi kedua, dan tidak membawa daftar nama peristiwa apa pun.
 */
import { businessDate, deviceCode, isTimestamp, isUuidV7, looksLikeIpAddress, outletCode, } from './events.js';
/** Guard {@link SyncId} (UUIDv7, §3). */
export function isSyncId(value) {
    return isUuidV7(value);
}
/** Konstruktor {@link SyncId}. Mengembalikan `undefined` bila ditolak — **tak pernah melempar**. */
export function syncId(value) {
    return isUuidV7(value) ? value : undefined;
}
/* ────────────────────────────────────────────────────────────────────────────
 * 1. Kesalahan kontrak — struktural, bukan pesan bebas (P2)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Kode kesalahan **jalur internal** (`assert*`, transisi, kursor).
 *
 * Kenapa enum dan bukan kalimat: `log_reprint` VTEC memakai alasan teks bebas ⇒ **18% kosong**,
 * sisanya `C` / `v` / `copy` / `duplikat` — kontrolnya benar, datanya tak bisa dianalisis (§10).
 */
export const SYNC_CONTRACT_ERROR_CODES = Object.freeze([
    'illegal_transition',
    'unknown_state',
    'revision_not_increased',
    'request_id_required',
    'request_id_changed',
    'retry_not_authorized',
    'ack_revision_mismatch',
    'cursor_rewind',
    'invalid_argument',
    'parse_failed',
]);
const SYNC_CONTRACT_ERROR_TAG = '@osbpos/contract:SyncContractError';
/**
 * Kesalahan yang **dilempar** jalur internal.
 *
 * P6 ("kontrol harus MEMAKSA, bukan sekadar mencatat"): transisi ilegal **melempar**, tidak
 * dicatat lalu diteruskan. Batas I/O tetap tak pernah melempar — di sana yang dipakai
 * {@link SyncParseResult}.
 */
export class SyncContractError extends Error {
    /**
     * Penanda runtime; dipakai {@link isSyncContractError} sebagai ganti `instanceof`, yang pecah
     * bila paket kontrak termuat dua kali di monorepo lima app (pola sama dengan `MoneyError`).
     */
    tag = SYNC_CONTRACT_ERROR_TAG;
    /** Klasifikasi struktural kesalahan. */
    code;
    /** Jalur field / konteks, mis. `records[2].id`. String kosong bila tak relevan. */
    path;
    constructor(code, message, path = '') {
        super(message);
        this.name = 'SyncContractError';
        this.code = code;
        this.path = path;
    }
}
/** Guard {@link SyncContractError} berbasis penanda properti, **bukan `instanceof`**. */
export function isSyncContractError(v) {
    return (typeof v === 'object' &&
        v !== null &&
        v.tag === SYNC_CONTRACT_ERROR_TAG);
}
function ok(value) {
    return { ok: true, value };
}
function fail(reason, path, detail) {
    return { ok: false, reason, path, detail };
}
function unwrap(result) {
    if (result.ok)
        return result.value;
    throw new SyncContractError('parse_failed', `${result.reason}: ${result.detail}`, result.path);
}
/* ────────────────────────────────────────────────────────────────────────────
 * 2. `SyncState` — mesin status kecil (§10)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Status sinkron sebuah RECORD (§10, persis daftar di SCHEMA-v0).
 *
 * | State | Makna |
 * |---|---|
 * | `local`  | lahir di terminal, belum masuk outbox. **Satu-satunya state kelahiran** |
 * | `queued` | baris outbox tertulis **di dalam transaksi bisnis** (SYNC §3 — pola outbox OSB Nexus; mustahil "terjual tapi tak pernah masuk antrean") |
 * | `sent`   | amplop terkirim & diterima transport = **fase 1** (padanan `Status`/`EndTime` `log_batchexport` VTEC) |
 * | `acked`  | portal **benar-benar menelan** = **fase 2** (padanan `ImportStatus`/`ImportEndTime`) |
 * | `failed` | ditolak permanen atau gagal transport; **bukan terminal** |
 *
 * ⭐ **Ack dua fase wajib ditiru** (SYNC §3): tanpa fase 2, kiriman bisa hilang di tengah tanpa
 * seorang pun tahu.
 *
 * ⚠️ **Terminal sejati hanya `acked` pada revisi tertentu.** Semua state lain punya jalan keluar —
 * antrean yang buntu diam-diam adalah pola kegagalan yang sudah terbukti mahal (SYNC §5).
 */
export const SYNC_STATES = Object.freeze([
    'local',
    'queued',
    'sent',
    'acked',
    'failed',
]);
const SYNC_STATE_SET = new Set(SYNC_STATES);
/** Guard {@link SyncState}. String di luar {@link SYNC_STATES} ditolak — termasuk `''` dan `'ACKED'`. */
export function isSyncState(v) {
    return typeof v === 'string' && SYNC_STATE_SET.has(v);
}
/**
 * **Satu-satunya state kelahiran** (§10 ∅ → `local`). Tak ada satu pun busur `→ local` di
 * {@link SYNC_TRANSITIONS}: record tak boleh "kembali jadi lokal" setelah pernah mengantre —
 * itu cara termudah kehilangan jejak apakah ia sudah pernah dikirim.
 */
export const SYNC_BIRTH_STATE = 'local';
/**
 * Tabel transisi SAH (§10).
 *
 * ```
 * ∅       → local     record dibuat di terminal (UUIDv7, §3)
 * local   → queued    baris outbox ditulis dalam trx bisnis
 * queued  → sent      amplop dikirim, transport menerima        [guard: request_id ada]
 * queued  → failed    validasi amplop gagal DI SISI OUTLET      (P6: tolak sebelum kirim)
 * sent    → acked     ack per-record `accepted` ATAU `duplicate`
 * sent    → failed    ack per-record `rejected`
 * sent    → queued    timeout / ack tak pernah datang           [guard: request_id DIPERTAHANKAN]
 * failed  → queued    jadwal ulang backoff / intervensi manusia [guard: retriable ATAU manusia]
 * acked   → queued    record bermutasi setelah ack              [guard: sync_revision NAIK]
 * ```
 *
 * ⛔ **`acked→failed` dilarang keras.** Sekali portal menelan, outlet tak boleh "membatalkan"
 * secara sepihak. Koreksi = **record baru + `corrects_id`**, bukan menimpa (SYNC §1: identitas
 * abadi; perbaikan data yang menulis ulang identitas persis penyebab `tranid` bentrok).
 *
 * ⚠️ Tabel ini **exhaustive atas {@link SYNC_STATES}** — menambah state memaksa menambah barisnya,
 * dan uji regresi memeriksanya.
 */
export const SYNC_TRANSITIONS = Object.freeze({
    local: Object.freeze(['queued']),
    queued: Object.freeze(['sent', 'failed']),
    sent: Object.freeze(['acked', 'failed', 'queued']),
    acked: Object.freeze(['queued']),
    failed: Object.freeze(['queued']),
});
function isNonBlank(v) {
    return typeof v === 'string' && v.trim().length > 0;
}
/**
 * Memeriksa satu transisi beserta guard-nya. **Tak pernah melempar** — untuk jalur yang ingin
 * tahu *kenapa* ditolak. {@link canTransition} dan {@link assertTransition} dibangun di atasnya.
 *
 * ⭐ `from`/`to` bertipe {@link SyncState}, **bukan `unknown`**: `SyncState` sengaja union literal
 * supaya salah ketik (`'aked'`, `'qeued'`) **gagal kompilasi** (P2). Menerima `unknown` di mesin
 * status paling kritis §10 membuang seluruh manfaat itu dan menunda kesalahan sampai runtime,
 * di mana ia hanya tampak sebagai `unknown_state`/`false`.
 *
 * Untuk nilai yang benar-benar datang dari kawat/DB, pakai {@link checkTransitionUnknown} —
 * ia memvalidasi bentuknya lebih dulu, dan namanya menyatakan bahwa masukannya belum tepercaya.
 */
export function checkTransition(from, to, ctx = {}) {
    return checkTransitionUnknown(from, to, ctx);
}
/**
 * Versi {@link checkTransition} untuk nilai **tak tepercaya** (kolom `sync_state` yang baru dibaca
 * dari DB/kawat): bentuknya diperiksa {@link isSyncState} lebih dulu, dan nilai di luar
 * {@link SYNC_STATES} ⇒ `unknown_state` (fail-closed, P6).
 */
export function checkTransitionUnknown(from, to, ctx = {}) {
    if (!isSyncState(from)) {
        return { ok: false, violation: 'unknown_state', detail: `state asal tak dikenal: ${String(from)}` };
    }
    if (!isSyncState(to)) {
        return { ok: false, violation: 'unknown_state', detail: `state tujuan tak dikenal: ${String(to)}` };
    }
    const allowed = SYNC_TRANSITIONS[from];
    if (!allowed.includes(to)) {
        return { ok: false, violation: 'illegal_transition', detail: `${from} → ${to} tidak ada di §10` };
    }
    // queued → sent : idempotensi wajib sudah punya kuncinya SEBELUM apa pun terkirim.
    if (from === 'queued' && to === 'sent') {
        if (!isNonBlank(ctx.request_id)) {
            return {
                ok: false,
                violation: 'request_id_required',
                detail: 'queued→sent butuh request_id (I1) — tak ada jalur "kirim sekali tanpa idempotensi"',
            };
        }
    }
    // sent → queued : retry. `request_id` DIPERTAHANKAN, `attempt_count` naik.
    // Membuat request_id baru tiap percobaan = idempotensi yang tak berfungsi sambil terlihat
    // berfungsi; satu retry lalu jadi penjualan ganda di laporan (SYNC §3).
    if (from === 'sent' && to === 'queued') {
        if (!isNonBlank(ctx.request_id) || !isNonBlank(ctx.previous_request_id)) {
            return {
                ok: false,
                violation: 'request_id_required',
                detail: 'sent→queued butuh request_id lama DAN baru untuk dibandingkan',
            };
        }
        if (ctx.request_id !== ctx.previous_request_id) {
            return {
                ok: false,
                violation: 'request_id_changed',
                detail: 'retry WAJIB memakai request_id yang sama',
            };
        }
    }
    // failed → queued : hanya bila kontrak menyatakan error-nya retriable, atau manusia memicunya.
    if (from === 'failed' && to === 'queued') {
        const retriable = ctx.last_error !== null && ctx.last_error !== undefined && ctx.last_error.retriable;
        if (!retriable && ctx.human_override !== true) {
            return {
                ok: false,
                violation: 'retry_not_authorized',
                detail: 'failed→queued butuh last_error.retriable === true atau human_override',
            };
        }
    }
    // acked → queued : SATU-SATUNYA jalan keluar dari `acked`, dan hanya bila revisi NAIK.
    // Tanpa revisi hanya ada dua pilihan buruk: larang perubahan pasca-ack (tak realistis), atau
    // izinkan bebas ⇒ record bisa dikirim ulang selamanya tanpa portal bisa membedakan
    // "kiriman ulang" dari "perubahan nyata". Kunci idempotensi record = (id, sync_revision).
    if (from === 'acked' && to === 'queued') {
        const before = ctx.from_revision;
        const after = ctx.to_revision;
        if (!isPositiveInt(before) || !isPositiveInt(after)) {
            return {
                ok: false,
                violation: 'revision_not_increased',
                detail: 'acked→queued butuh from_revision & to_revision (integer ≥ 1)',
            };
        }
        if (after <= before) {
            return {
                ok: false,
                violation: 'revision_not_increased',
                detail: `sync_revision wajib NAIK (${String(before)} → ${String(after)})`,
            };
        }
    }
    return { ok: true };
}
/** Apakah transisi sah (termasuk guard-nya)? Lihat {@link checkTransition} untuk alasan penolakan. */
export function canTransition(from, to, ctx = {}) {
    return checkTransitionUnknown(from, to, ctx).ok;
}
/** Versi {@link canTransition} untuk nilai tak tepercaya. Lihat {@link checkTransitionUnknown}. */
export function canTransitionUnknown(from, to, ctx = {}) {
    return checkTransitionUnknown(from, to, ctx).ok;
}
/**
 * Versi **MEMAKSA** {@link canTransition} — melempar {@link SyncContractError} pada transisi ilegal.
 *
 * P6: kontrol yang hanya mencatat bukan kontrol. Ini jalur internal (outlet-server & portal),
 * bukan batas I/O — di batas I/O pakai `parse*` yang mengembalikan {@link SyncParseResult}.
 * Nilai dari kawat diperiksa {@link checkTransitionUnknown} lebih dulu, bukan dilempar ke sini
 * lewat cast.
 */
export function assertTransition(from, to, ctx = {}) {
    const check = checkTransitionUnknown(from, to, ctx);
    if (!check.ok) {
        throw new SyncContractError(check.violation, check.detail, 'sync_state');
    }
}
/* ────────────────────────────────────────────────────────────────────────────
 * 3. Taksonomi: kind, arah, hasil batch, alasan
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Jenis isi batch NAIK (§10). **Satu `kind` per batch (homogen)** — batch campur membuat ack
 * ambigu, retry sebagian mustahil, dan retensi per-kind (P9) tak bisa diterapkan.
 */
export const SYNC_KINDS = Object.freeze([
    'order',
    'order_tender',
    'order_event',
    'stock_movement',
    'shift',
    'attendance',
    'event',
]);
const SYNC_KIND_SET = new Set(SYNC_KINDS);
/** Guard {@link SyncKind}. */
export function isSyncKind(v) {
    return typeof v === 'string' && SYNC_KIND_SET.has(v);
}
/**
 * Jenis master arah TURUN (SYNC §2).
 *
 * ⛔ **Tidak ada nilai untuk ketersediaan/countdown stok.** `productcountdownstock` = **lokal
 * outlet, tidak disinkronkan** (bukti: 6 baris di `menya_smb`, **0** di HQ). "86" adalah urusan
 * outlet, dan itu benar.
 */
export const MASTER_KINDS = Object.freeze([
    'product',
    'product_price',
    'tax_code',
    'payment_method',
    'modifier',
    'combo',
    'staff_ref',
]);
const MASTER_KIND_SET = new Set(MASTER_KINDS);
/** Guard {@link MasterKind}. */
export function isMasterKind(v) {
    return typeof v === 'string' && MASTER_KIND_SET.has(v);
}
/** Arah sebuah batch (§10 `sync_batch.direction`). */
export const SYNC_DIRECTIONS = Object.freeze(['up', 'down']);
/** Hasil akhir sebuah batch di sisi portal. **Turunan** dari hitungan — lihat {@link deriveBatchResult}. */
export const BATCH_RESULTS = Object.freeze(['applied', 'partial', 'rejected']);
const BATCH_RESULT_SET = new Set(BATCH_RESULTS);
/** Guard {@link BatchResult}. */
export function isBatchResult(v) {
    return typeof v === 'string' && BATCH_RESULT_SET.has(v);
}
/** Hasil batch pada RINGKASAN (§10 `sync_batch.result`) — termasuk `pending` sebelum ack tiba. */
export const SYNC_BATCH_RESULTS = Object.freeze([
    'pending',
    'applied',
    'partial',
    'rejected',
]);
/**
 * Hasil ack **per record**.
 *
 * ⭐ Ketiganya memetakan ke dua state saja (`duplicate` dan `accepted` sama-sama → `acked`), jadi
 * godaan meleburnya besar. **Dua-duanya salah:**
 *
 * - **`duplicate` dilaporkan sebagai `accepted`** ⇒ hilang sinyal bahwa outlet mengirim ulang
 *   berlebihan. Duplikat yang meningkat adalah **gejala** (ack tak sampai balik, jendela
 *   idempotensi kesempitan, outbox tak terpangkas), bukan keadaan normal. Melebur = membuang
 *   satu-satunya alat ukur kesehatan retry. Bukti apa yang terjadi bila kegagalan tak terlihat
 *   (R2 §10b): `RewardPointHistory` Arena — **4.547 baris belum tersinkron, 719 error,
 *   bertahun-tahun, tak ada yang tahu**.
 * - **`duplicate` dilaporkan sebagai `rejected`** ⇒ outlet menandai `failed` dan mengulang
 *   selamanya; lebih buruk, operator akan "memperbaiki" dengan mengirim ulang memakai identitas
 *   baru ⇒ **penjualan ganda**. SYNC §3: *"pengiriman ulang batch yang sama = tanpa efek"* —
 *   prasyarat, bukan optimasi.
 *
 * ⇒ `duplicate` = **"sudah ada, tanpa efek, dan itu BENAR"**. Sukses bagi mesin status, dihitung
 * terpisah bagi kesehatan.
 */
export const ACK_OUTCOMES = Object.freeze(['accepted', 'duplicate', 'rejected']);
const ACK_OUTCOME_SET = new Set(ACK_OUTCOMES);
/** Guard {@link AckOutcome}. */
export function isAckOutcome(v) {
    return typeof v === 'string' && ACK_OUTCOME_SET.has(v);
}
/**
 * Kenapa sebuah record dianggap duplikat.
 *
 * | Nilai | Makna |
 * |---|---|
 * | `same_revision` | `(id, revision)` persis ini sudah ditelan |
 * | `superseded`    | portal sudah punya revisi **lebih tinggi**; kiriman ini basi ⇒ no-op |
 *
 * `superseded` menangani kedatangan tak berurutan **tanpa** menolak — menolak yang basi akan
 * membuat outlet mengulang selamanya sesuatu yang sudah tak relevan.
 */
export const DUPLICATE_REASONS = Object.freeze(['same_revision', 'superseded']);
const DUPLICATE_REASON_SET = new Set(DUPLICATE_REASONS);
/** Guard {@link DuplicateReason}. */
export function isDuplicateReason(v) {
    return typeof v === 'string' && DUPLICATE_REASON_SET.has(v);
}
/**
 * Alasan penolakan — **ENUM, bukan teks bebas** (P2).
 *
 * Bukti kenapa enum: `log_reprint` VTEC memakai alasan teks bebas ⇒ **18% kosong**, sisanya
 * `C` / `v` / `copy` / `duplikat`. Kontrolnya benar, datanya tak bisa dianalisis (§10).
 * `detail` boleh bebas — tapi hanya untuk mata manusia.
 */
export const REJECT_REASONS = Object.freeze([
    'missing_request_id',
    'request_id_reuse_conflict',
    'schema_invalid',
    'schema_too_new',
    'count_mismatch',
    'empty_batch',
    'duplicate_id_in_batch',
    'unknown_outlet',
    'unknown_device',
    'lease_lost',
    'outlet_mismatch',
    'missing_reference',
    'business_rule',
    'internal_error',
]);
const REJECT_REASON_SET = new Set(REJECT_REASONS);
/** Guard {@link RejectReason}. */
export function isRejectReason(v) {
    return typeof v === 'string' && REJECT_REASON_SET.has(v);
}
/**
 * ⭐ `retriable` **ditetapkan di kontrak, tidak ditebak klien.**
 *
 * Klien yang menebak akan menebak berbeda di tiap lapis, dan itu persis penyimpangan tiga-lapis
 * yang membuat paket ini ada. Yang berhenti otomatis hanya `retriable: false` — dan itu **wajib**
 * memunculkan kondisi alarm (lihat {@link evaluateHealth}), bukan diam.
 *
 * Alasan tiap nilai:
 * - `internal_error`, `lease_lost` → **true**: keadaan portal/lease bisa pulih sendiri.
 * - `missing_reference` → **true**: acuan bisa tiba di batch lain (kedatangan tak berurutan);
 *   menolak permanen berarti kehilangan record yang sebenarnya sah.
 * - sisanya **false**: bug klien atau salah konfigurasi. Mengulanginya hanya memutar tanpa hasil,
 *   dan itulah kegagalan senyap yang R2 larang.
 */
export const REJECT_RETRIABLE = Object.freeze({
    missing_request_id: false,
    request_id_reuse_conflict: false,
    schema_invalid: false,
    schema_too_new: false,
    count_mismatch: false,
    empty_batch: false,
    duplicate_id_in_batch: false,
    unknown_outlet: false,
    unknown_device: false,
    lease_lost: true,
    outlet_mismatch: false,
    missing_reference: true,
    business_rule: false,
    internal_error: true,
});
/** Apakah penolakan `reason` boleh dijadwal ulang? Sumbernya {@link REJECT_RETRIABLE}. */
export function isRetriableReject(reason) {
    return REJECT_RETRIABLE[reason];
}
/**
 * Kegagalan **transport** — beda kamar dengan {@link RejectReason} (yang datang dari portal).
 * Amplop yang tak pernah sampai belum tentu salah isinya.
 */
export const TRANSPORT_ERROR_CODES = Object.freeze([
    'timeout',
    'network_unreachable',
    'tls_error',
    'http_4xx',
    'http_5xx',
    'aborted',
    'unknown',
]);
const TRANSPORT_ERROR_SET = new Set(TRANSPORT_ERROR_CODES);
/** Guard {@link TransportErrorCode}. */
export function isTransportErrorCode(v) {
    return typeof v === 'string' && TRANSPORT_ERROR_SET.has(v);
}
/**
 * `retriable` untuk kegagalan transport — juga **dari kontrak**.
 *
 * `http_4xx` dan `tls_error` = salah konfigurasi/kredensial ⇒ mengulang tak menyembuhkan, tapi
 * **wajib** memunculkan alarm. `unknown` = true: menyerah berarti kehilangan penjualan, dan itu
 * harga yang jauh lebih mahal daripada satu percobaan sia-sia.
 */
export const TRANSPORT_RETRIABLE = Object.freeze({
    timeout: true,
    network_unreachable: true,
    tls_error: false,
    http_4xx: false,
    http_5xx: true,
    aborted: true,
    unknown: true,
});
/**
 * Membentuk {@link SyncError} dengan `retriable` **diambil dari kontrak**.
 *
 * Ini satu-satunya konstruktor yang boleh dipakai; membuat objek `SyncError` dengan tangan
 * membuka pintu bagi klien untuk menebak `retriable` sendiri, dan tiga lapis akan menebak beda.
 */
export function makeSyncError(code, message, at, http_status = null) {
    const retriable = isRejectReason(code)
        ? REJECT_RETRIABLE[code]
        : isTransportErrorCode(code)
            ? TRANSPORT_RETRIABLE[code]
            : undefined;
    if (retriable === undefined) {
        throw new SyncContractError('invalid_argument', `kode kesalahan tak dikenal: ${String(code)}`, 'code');
    }
    return Object.freeze({ code, message, retriable, at, http_status });
}
/** Status awal sebuah record baru (§10 default: `local` / revisi 1 / nol percobaan). */
export function initialRecordSyncStatus() {
    return Object.freeze({
        sync_state: SYNC_BIRTH_STATE,
        sync_revision: 1,
        exported_at: null,
        acked_at: null,
        attempt_count: 0,
        last_error: null,
        last_batch_id: null,
        next_attempt_at: null,
    });
}
/**
 * `batch_result` sebagai **turunan** hitungan (§10):
 * `applied` ⇔ `rejected = 0` · `rejected` ⇔ `accepted + duplicate = 0` · selebihnya `partial`.
 *
 * Ringkasan nol-semua ⇒ `rejected`: itu bentuk ack untuk amplop cacat (`results: []`), dan
 * "tak menelan apa pun" memang bukan `applied`.
 */
export function deriveBatchResult(summary) {
    assertCount(summary.accepted, 'summary.accepted');
    assertCount(summary.duplicate, 'summary.duplicate');
    assertCount(summary.rejected, 'summary.rejected');
    if (summary.accepted + summary.duplicate === 0)
        return 'rejected';
    if (summary.rejected === 0)
        return 'applied';
    return 'partial';
}
function assertCount(v, path) {
    if (typeof v !== 'number' || !Number.isSafeInteger(v) || v < 0) {
        throw new SyncContractError('invalid_argument', `${path} wajib integer ≥ 0`, path);
    }
}
/**
 * Menerapkan satu {@link AckRecordResult} ke {@link RecordSyncStatus}.
 *
 * - `accepted` **dan** `duplicate` ⇒ `acked` (`duplicate` bukan kegagalan — lihat {@link ACK_OUTCOMES}).
 * - `rejected` ⇒ `failed` + `last_error` terisi; **tidak pernah** `acked`.
 * - Alasan non-retriable ⇒ `next_attempt_at` tetap `null` **apa pun** yang dioper pemanggil.
 *
 * Melempar bila revisinya tak cocok — ack untuk revisi lain bukan ack untuk record ini, dan
 * menerapkannya diam-diam adalah kehilangan senyap.
 *
 * ### ⭐ IDEMPOTEN pada revisi yang sama (SYNC §3)
 * SYNC §3 menyatakan *"pengiriman ulang batch yang sama = tanpa efek"*, dan {@link decideIdempotency}
 * memang memutar ulang **ack yang identik**. Maka menerapkan ack yang sama dua kali tak boleh
 * meledak: konsumen ack adalah perulangan `for (r of ack.results) applyAck(...)` — sekali ia
 * melempar di tengah, **sisa record dalam batch itu tak pernah diproses**, dan retry (yang oleh
 * desain ini dinyatakan normal) berubah jadi kegagalan.
 *
 * ⇒ Dua keadaan diperlakukan sebagai **no-op**, bukan pelanggaran:
 * | Keadaan | Perlakuan |
 * |---|---|
 * | sudah `acked`, ack `accepted`/`duplicate`, revisi sama | kembalikan status apa adanya (replay) |
 * | sudah `failed`, ack `rejected`, revisi sama | kembalikan status apa adanya (replay) |
 * | masih `queued` (sudah di-requeue karena timeout), ack tiba terlambat | kembalikan status apa adanya |
 *
 * Kasus ketiga sejalan dengan {@link unresolvedIds}: **klien tidak menebak**. Percobaan kirim yang
 * sedang berjalan akan dijawab `duplicate` oleh portal dan record itu ber-ack lewat jalur normal
 * `queued → sent → acked`.
 *
 * Yang **tetap melempar**: state asal yang benar-benar mustahil (`local` — belum pernah masuk
 * antrean), dan seluruh transisi yang dilarang §10 (mis. `acked → failed`).
 */
export function applyAck(status, result, ctx) {
    if (result.revision !== status.sync_revision) {
        throw new SyncContractError('ack_revision_mismatch', `ack revisi ${String(result.revision)} tak cocok dengan record revisi ${String(status.sync_revision)}`, 'revision');
    }
    const batchId = ctx.batch_id ?? status.last_batch_id;
    const settled = result.outcome === 'accepted' || result.outcome === 'duplicate';
    // Replay / ack telat — no-op. Revisi sudah dipastikan sama di atas.
    if (settled && status.sync_state === 'acked')
        return status;
    if (!settled && status.sync_state === 'failed')
        return status;
    if (settled && status.sync_state === 'queued')
        return status;
    if (settled) {
        assertTransition(status.sync_state, 'acked');
        return Object.freeze({
            ...status,
            sync_state: 'acked',
            acked_at: ctx.acked_at,
            last_error: null,
            last_batch_id: batchId,
            next_attempt_at: null,
        });
    }
    assertTransition(status.sync_state, 'failed');
    const reason = result.reason;
    if (!isRejectReason(reason)) {
        throw new SyncContractError('invalid_argument', 'outcome "rejected" wajib menyertakan RejectReason (P2 — bukan teks bebas, bukan null)', 'reason');
    }
    const error = makeSyncError(reason, result.detail ?? reason, ctx.acked_at, null);
    return Object.freeze({
        ...status,
        sync_state: 'failed',
        last_error: error,
        last_batch_id: batchId,
        // P6: alasan non-retriable TIDAK boleh dijadwalkan ulang, betapapun pemanggil memintanya.
        next_attempt_at: error.retriable ? (ctx.next_attempt_at ?? null) : null,
    });
}
/**
 * Aturan toko idempotensi (§10).
 *
 * - `request_id` berulang + `payload_hash` **sama** ⇒ **kembalikan ack yang SAMA** (replay),
 *   jangan proses ulang. SYNC §3: *"pengiriman ulang batch yang sama = tanpa efek."*
 * - `request_id` berulang + `payload_hash` **berbeda** ⇒ `request_id_reuse_conflict`,
 *   **non-retriable**. Itu bug klien; menelannya diam-diam = kehilangan data yang tak akan pernah
 *   ketahuan.
 *
 * ⚠️ Retensi toko ini adalah **setelan** ({@link RetentionPolicy}), bukan konstanta: ia wajib
 * melebihi *"berapa lama terminal boleh jualan tanpa server"* (🔶#4) — angka yang belum diputuskan.
 */
export function decideIdempotency(existing, payload_hash) {
    if (existing === null)
        return { kind: 'fresh' };
    if (existing.payload_hash === payload_hash)
        return { kind: 'replay', ack: existing.ack };
    return { kind: 'conflict', reason: 'request_id_reuse_conflict', retriable: false };
}
/** Cara portal menjawab tarikan master. */
export const DOWN_MODES = Object.freeze(['snapshot', 'delta']);
const DOWN_MODE_SET = new Set(DOWN_MODES);
/** Guard {@link DownMode}. */
export function isDownMode(v) {
    return typeof v === 'string' && DOWN_MODE_SET.has(v);
}
/**
 * Asal-usul sebuah record master — pengganti struktural `AddingFromBranch` VTEC.
 * Tanpa penanda ini, tak ada yang tahu asal-usul data saat terjadi konflik (SYNC §2).
 */
export const DOWN_ORIGINS = Object.freeze(['central', 'branch']);
const DOWN_ORIGIN_SET = new Set(DOWN_ORIGINS);
/** Guard {@link DownOrigin}. */
export function isDownOrigin(v) {
    return typeof v === 'string' && DOWN_ORIGIN_SET.has(v);
}
/**
 * Portal boleh **MEMAKSA reset** (P6): bila kursor tak ada, atau `snapshot_version`-nya di bawah
 * minimum yang masih didukung, jawabannya `'snapshot'` — dan klien **wajib** mengganti seluruh isi
 * kind itu.
 */
export function decideDownMode(cursor, min_supported_snapshot_version) {
    if (!isPositiveInt(min_supported_snapshot_version)) {
        throw new SyncContractError('invalid_argument', 'min_supported_snapshot_version wajib integer ≥ 1', 'min_supported_snapshot_version');
    }
    if (cursor === null)
        return 'snapshot';
    return cursor.snapshot_version < min_supported_snapshot_version ? 'snapshot' : 'delta';
}
/**
 * Memajukan kursor subscriber. **Hanya maju.**
 *
 * UUIDv7 terurut waktu (§3) ⇒ perbandingan leksikografis string = perbandingan waktu. Mundur
 * berarti mengirim ulang peristiwa yang sudah dilewati — dan bagi subscriber yang tak idempoten,
 * itu duplikasi fakta bisnis di sistem orang lain.
 */
export function advanceSubscriberCursor(cursor, next_event_id, at) {
    if (!isSyncId(next_event_id)) {
        throw new SyncContractError('invalid_argument', 'next_event_id wajib UUIDv7', 'next_event_id');
    }
    const last = cursor.last_event_id;
    if (last !== null && next_event_id <= last) {
        throw new SyncContractError('cursor_rewind', `kursor subscriber hanya boleh maju (${last} → ${next_event_id})`, 'last_event_id');
    }
    return Object.freeze({ ...cursor, last_event_id: next_event_id, last_acked_at: at });
}
/* ────────────────────────────────────────────────────────────────────────────
 * 12. Retensi (P9) — angka = SETELAN, bukan konstanta
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Kategori retensi (§10 · SYNC §3).
 *
 * | Kategori | Aturan |
 * |---|---|
 * | `sync_batch` | boleh dibuang setelah periode **direkonsiliasi & ditutup** — ia **bukan ledger** |
 * | `idempotency_store` | **≥ jendela retry maksimum**, wajib melebihi 🔶#4 |
 * | `outbox_acked` | boleh dipadatkan/dihapus setelah ack |
 * | `record_sync_status` | ikut umur record induknya |
 * | `event_outbox` | setelah **semua** subscriber melewatinya + retensi minimum |
 *
 * ⛔ `order` · `order_tender` · `stock_movement` **PENUH — jangan pernah dipangkas** (§10);
 * karena itu ketiganya **tidak ada** di daftar ini.
 *
 * Bukti kenapa retensi jadi prinsip, bukan tuning: `kds_detail` = **110 MB dari 409 MB** DB outlet,
 * 11 bulan tanpa trimming, **tanpa indeks pada `KDSStatus`**, 91% barisnya sudah selesai.
 */
export const RETENTION_CATEGORIES = Object.freeze([
    'sync_batch',
    'idempotency_store',
    'outbox_acked',
    'record_sync_status',
    'event_outbox',
]);
/** Parser {@link RetentionPolicy} untuk setelan yang datang dari luar. Tak melempar. */
export function parseRetentionPolicy(u) {
    const o = asObject(u);
    if (o === undefined)
        return fail('schema_invalid', '', 'retention policy wajib objek');
    const days = o['retention_days'];
    if (!isPositiveInt(days)) {
        return fail('schema_invalid', 'retention_days', 'retention_days wajib integer ≥ 1');
    }
    const extra = firstUnknownKey(o, RETENTION_POLICY_KEYS);
    if (extra !== undefined)
        return fail('schema_invalid', extra, 'field tak dikenal');
    return ok(Object.freeze({ retention_days: days }));
}
const RETENTION_POLICY_KEYS = new Set(['retention_days']);
/**
 * Memvalidasi setelan retensi lengkap. Melempar bila ada kategori yang hilang atau nilainya `< 1`.
 * Konsistensi 🔶#4 (toko idempotensi ≥ jendela retry) **tak** bisa dicek di sini — angkanya belum ada.
 */
export function assertRetentionSettings(settings) {
    for (const category of RETENTION_CATEGORIES) {
        const policy = settings[category];
        if (!isPositiveInt(policy.retention_days)) {
            throw new SyncContractError('invalid_argument', `retention_days untuk "${category}" wajib integer ≥ 1`, category);
        }
    }
}
/**
 * Menghitung jeda percobaan ke-`attempt` (1-based). **Fungsi murni** — bukan penjadwal, bukan
 * transport.
 *
 * - Monoton naik sampai plafon; **tak pernah** melebihi `max_delay_ms`.
 * - `attempt` besar **tak overflow**: perkalian dihentikan begitu menyentuh plafon.
 * - `jitter: 'full'` ⇒ hasil selalu di `[0, delay]`.
 *
 * @param random sumber acak yang bisa disuntik agar uji deterministik; default `Math.random`.
 */
export function nextDelayMs(policy, attempt, random = Math.random) {
    if (!isPositiveInt(attempt)) {
        throw new SyncContractError('invalid_argument', 'attempt wajib integer ≥ 1', 'attempt');
    }
    if (!isPositiveInt(policy.base_delay_ms)) {
        throw new SyncContractError('invalid_argument', 'base_delay_ms wajib integer ≥ 1', 'base_delay_ms');
    }
    if (!isPositiveInt(policy.max_delay_ms) || policy.max_delay_ms < policy.base_delay_ms) {
        throw new SyncContractError('invalid_argument', 'max_delay_ms wajib integer ≥ base_delay_ms', 'max_delay_ms');
    }
    if (typeof policy.factor !== 'number' || !Number.isFinite(policy.factor) || policy.factor < 1) {
        throw new SyncContractError('invalid_argument', 'factor wajib angka berhingga ≥ 1', 'factor');
    }
    // Bentuk tertutup, bukan perulangan: `attempt` boleh sebesar apa pun (outlet yang luring
    // berhari-hari) tanpa membuat fungsi ini berputar lama, dan `Infinity` langsung terjepit plafon.
    // ⚠️ Float dipakai SENGAJA di sini — ini jeda milidetik, **bukan** uang (P1 tak berlaku).
    const growth = Math.pow(policy.factor, attempt - 1);
    let delay = policy.base_delay_ms * growth;
    if (!Number.isFinite(delay) || delay > policy.max_delay_ms) {
        delay = policy.max_delay_ms;
    }
    const capped = Math.min(Math.floor(delay), policy.max_delay_ms);
    if (policy.jitter === 'none')
        return capped;
    const r = random();
    const clamped = typeof r === 'number' && Number.isFinite(r) ? Math.min(Math.max(r, 0), 1) : 0;
    return Math.min(Math.floor(clamped * capped), capped);
}
/** Kondisi alarm — struktural, bisa dipantau & di-alarm-kan (R2). */
export const SYNC_ALARM_CODES = Object.freeze([
    'pending_backlog',
    'pending_too_old',
    'no_success_too_long',
    'never_succeeded',
    'failures_present',
]);
function epochMs(ts) {
    if (ts === null)
        return undefined;
    const n = Date.parse(ts);
    return Number.isFinite(n) ? n : undefined;
}
/**
 * ⭐ **Alarm berbasis KONDISI, bukan peristiwa** (R2 §10b).
 *
 * Kegagalan senyap **tak memancarkan peristiwa apa pun** — itu justru definisinya. Karena itu
 * fungsi ini hanya membaca **keadaan**: `pending_count`, umur antrean tertua, dan berapa lama sejak
 * sukses terakhir.
 *
 * Tiga bukti dari lapangan kenapa ini aturan dan bukan fitur:
 * `RewardPointHistory` (4.547 pending + 719 error, bertahun-tahun, tak ada yang tahu) ·
 * `logSelisih` (berhenti April 2024, 27 entri, padahal selisihnya masih terjadi) ·
 * `OrderDetailStock` (mati 2022, tak ada yang menyadari).
 *
 * ⚠️ Menampilkan antrean yang menumpuk kepada staf (SYNC §5) adalah tugas UI; kontrak hanya
 * menyediakan bentuk & putusannya.
 */
export function evaluateHealth(health, thresholds) {
    assertCount(health.pending_count, 'pending_count');
    assertCount(health.failed_count, 'failed_count');
    const now = epochMs(thresholds.now);
    if (now === undefined) {
        throw new SyncContractError('invalid_argument', 'thresholds.now wajib TS UTC yang sah', 'now');
    }
    const alarms = [];
    if (health.pending_count > thresholds.max_pending)
        alarms.push('pending_backlog');
    const oldest = epochMs(health.oldest_pending_at);
    if (oldest !== undefined && now - oldest > thresholds.max_pending_age_ms) {
        alarms.push('pending_too_old');
    }
    const lastSuccess = epochMs(health.last_success_at);
    if (lastSuccess === undefined) {
        // Belum pernah sukses SAMA SEKALI padahal ada yang mengantre/gagal: bentuk paling murni dari
        // "integrasi yang berjalan tanpa menghasilkan apa pun terlihat persis seperti yang sehat".
        if (health.pending_count > 0 || health.failed_count > 0)
            alarms.push('never_succeeded');
    }
    else if (now - lastSuccess > thresholds.max_since_success_ms) {
        alarms.push('no_success_too_long');
    }
    if (health.failed_count > 0)
        alarms.push('failures_present');
    return Object.freeze({ alarming: alarms.length > 0, alarms: Object.freeze(alarms) });
}
function asObject(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return undefined;
    return v;
}
function isPositiveInt(v) {
    return typeof v === 'number' && Number.isSafeInteger(v) && v >= 1;
}
/** String tanpa whitespace, tak kosong, dalam batas panjang. Dipakai untuk token wire. */
function isToken(v, max) {
    return typeof v === 'string' && v.length > 0 && v.length <= max && !/\s/.test(v);
}
function firstUnknownKey(o, allowed) {
    for (const key of Object.keys(o)) {
        if (!allowed.has(key))
            return key;
    }
    return undefined;
}
/** Semver paket kontrak. Longgar pada pra-rilis, ketat pada bentuk `x.y.z`. */
const SEMVER_RE = /^(?:0|[1-9][0-9]*)\.(?:0|[1-9][0-9]*)\.(?:0|[1-9][0-9]*)(?:-[0-9A-Za-z.-]+)?$/;
/**
 * `payload_hash` = **sha-256 heks huruf kecil**, 64 karakter.
 *
 * Bentuknya dikunci di kontrak agar dua lapis tak memilih algoritma berbeda dan diam-diam gagal
 * mendeteksi `request_id` yang dipakai ulang untuk isi berbeda. Menghitungnya urusan transport;
 * berkas ini nol dependensi dan tak menyentuh kripto.
 */
const PAYLOAD_HASH_RE = /^[0-9a-f]{64}$/;
/** Bentuk `endpoint` — path-like ASCII tanpa whitespace (cakupan idempotensi, §10b). */
const ENDPOINT_RE = /^[A-Za-z0-9][A-Za-z0-9._\-/]{0,119}$/;
const REQUEST_ID_MAX = 128;
const LEASE_TOKEN_MAX = 200;
const DETAIL_MAX = 500;
const PAYLOAD_MAX_DEPTH = 32;
/**
 * ⭐ **I8 sebagai kontrol struktural, bukan komentar** (P1) — dan aturannya **DAFTAR-IZIN**,
 * bukan daftar-tolak.
 *
 * Sebuah `number` di payload hanya sah bila namanya **pencacah/urutan/durasi yang dikenal**.
 * Semua nama lain ditolak, apa pun nilainya.
 *
 * ### Kenapa arahnya dibalik (dan ini bukan selera)
 * Versi sebelumnya menolak `number` hanya pada ~20 akhiran nama bernuansa uang
 * (`*_total`, `*_amount`, `*_qty`, …). Nama field yang membawa uang **tidak terbatas**, sementara
 * pencacah **sedikit dan diketahui** — jadi daftar-tolak selalu bocor. Yang bocor bukan teoretis:
 * `counted_cash`, `expected_cash`, `variance`, `total_variance`, `taxable_base`, `amount_refunded`,
 * `gross_reversed`, dan `opening_float` semuanya nama `MoneyString` **nyata** di `events.ts` dan
 * tak satu pun tertangkap. Nilai rupiah nyaris selalu bulat ⇒ `counted_cash: 1000000` lolos
 * sebagai integer aman. Itu persis pola `osbhqpos` yang melanggar P1 di **68 kolom**.
 *
 * ⚠️ Konsekuensi yang disengaja: nama pencacah **baru** ditolak sampai didaftarkan di sini —
 * satu tempat, terlihat di diff, dan gagal-tutup (P6). Kebalikannya — uang yang lolos diam-diam —
 * baru ketahuan saat rekonsiliasi, berbulan-bulan kemudian.
 */
const COUNTER_FIELD_RE = /(?:^|_)(attempt|attempts|count|days|hours|idx|index|minutes|ms|no|order|revision|round|seats|seconds|select|seq|sequence|status|version)$/;
/**
 * Apakah nama field ini **pencacah** — satu-satunya kelas nilai yang boleh muncul sebagai JSON
 * `number` di payload (§2: `MONEY`/`QTY`/`RATE` selalu string desimal kanonik).
 *
 * Contoh yang lolos: `revision` · `schema_version` · `record_count` · `line_no` · `send_round` ·
 * `guest_count` · `shift_count` · `open_order_count` · `attempt` · `sequence` · `sort_order` ·
 * `min_select` · `timeout_minutes` · `http_status`.
 */
export function isCounterFieldName(name) {
    return COUNTER_FIELD_RE.test(name);
}
/**
 * Menelusuri payload dan mencari pelanggaran presisi kawat (I8 · P1). Mengembalikan **jalur**
 * pelanggaran pertama, atau `undefined` bila bersih.
 *
 * Tiga aturan, semuanya turunan langsung dari `money.ts`:
 * 1. **`number` hanya sah di field ber-nama pencacah** ({@link isCounterFieldName}) — lihat alasan
 *    pembalikan arah di sana. `12345` sebagai `counted_cash` sama salahnya dengan `12345.67`:
 *    bentuk kanoniknya `"12345.0000"`.
 * 2. **Pencacah pun wajib integer aman** — `1.5` sebagai `line_no` tetap ditolak.
 * 3. **`number` tanpa nama** (elemen array yang induknya bukan pencacah, atau payload yang isinya
 *    angka telanjang) ditolak: tak ada yang bisa membuktikan ia bukan uang.
 *
 * `bigint` juga ditolak: ia bukan JSON, dan `money.ts` G4/G5 sengaja membiarkan
 * `JSON.stringify` gagal keras alih-alih menambal `BigInt.prototype.toJSON`.
 */
function scanWirePrecisionViolation(value, path, fieldName, depth = 0) {
    if (depth > PAYLOAD_MAX_DEPTH)
        return path;
    if (typeof value === 'bigint')
        return path;
    if (typeof value === 'number') {
        if (fieldName === undefined || !isCounterFieldName(fieldName))
            return path;
        return Number.isSafeInteger(value) ? undefined : path;
    }
    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i += 1) {
            // Elemen array mewarisi nama field induknya: `line_nos: [1,2]` sah, `amounts: [1,2]` tidak.
            const hit = scanWirePrecisionViolation(value[i], `${path}[${String(i)}]`, fieldName, depth + 1);
            if (hit !== undefined)
                return hit;
        }
        return undefined;
    }
    const o = asObject(value);
    if (o === undefined)
        return undefined;
    for (const key of Object.keys(o)) {
        const childPath = path === '' ? key : `${path}.${key}`;
        const hit = scanWirePrecisionViolation(o[key], childPath, key, depth + 1);
        if (hit !== undefined)
            return hit;
    }
    return undefined;
}
/* ── Amplop naik ─────────────────────────────────────────────────────────── */
const UP_ENVELOPE_KEYS = new Set([
    'contract_version',
    'envelope_kind',
    'request_id',
    'batch_id',
    'endpoint',
    'payload_hash',
    'outlet_code',
    'device_code',
    'lease_token',
    'kind',
    'schema_version',
    'record_count',
    'key_range',
    'business_date_range',
    'created_at',
    'sent_at',
    'attempt',
    'records',
]);
const UP_RECORD_KEYS = new Set(['id', 'revision', 'business_date', 'payload']);
/**
 * Parser amplop NAIK — **tak pernah melempar**, mengembalikan {@link SyncParseResult}.
 *
 * Menegakkan seluruh invarian §10 **di sisi mana pun ia dipanggil** (P6: outlet menolak sebelum
 * kirim, portal menolak sebelum menelan):
 *
 * | # | Invarian | Alasan |
 * |---|---|---|
 * | I1 | `request_id` ada & tidak kosong/whitespace | idempotensi |
 * | I2 | `record_count === records.length` | klaim ≠ isi = bug yang harus berisik |
 * | I3 | `records.length > 0` | **P8** — bukti: 135.593 dokumen backflush VTEC, **92% kosong**, terus sampai Juli 2026: terlihat bekerja, tak menghitung apa pun |
 * | I4 | `key_range` **dihitung** dari `records` | UUIDv7 terurut waktu ⇒ rentang bermakna |
 * | I5 | tak ada `id` duplikat dalam satu amplop | |
 * | I6 | seluruh record milik `outlet_code` di header | |
 * | I7 | `schema_version` dikenal; lebih tinggi ⇒ tolak seluruhnya | |
 * | I8 | tiap nilai MONEY/QTY lolos aturan kawat `money.ts` | **P1** — `osbhqpos` melanggar di 68 kolom |
 *
 * ⚠️ I6 diperiksa **oportunistik**: payload generik, jadi yang bisa dibandingkan hanyalah field
 * `outlet_code` bila payload memang membawanya (semua peristiwa §10b membawanya). Payload yang tak
 * membawanya tidak bisa berbohong tentang outlet, jadi tak ada yang perlu ditolak.
 */
export function parseUpBatchEnvelope(u, options) {
    const o = asObject(u);
    if (o === undefined)
        return fail('schema_invalid', '', 'amplop naik wajib objek JSON');
    // ── I1 lebih dulu dari apa pun: tanpa kunci idempotensi, tak ada yang boleh diproses.
    const requestId = o['request_id'];
    if (requestId === undefined || requestId === null) {
        return fail('missing_request_id', 'request_id', 'request_id WAJIB — tak ada jalur tanpa idempotensi');
    }
    if (typeof requestId !== 'string' || requestId.trim().length === 0) {
        return fail('missing_request_id', 'request_id', 'request_id kosong atau hanya whitespace');
    }
    if (!isToken(requestId, REQUEST_ID_MAX)) {
        return fail('missing_request_id', 'request_id', 'request_id memuat whitespace atau terlalu panjang');
    }
    const unknownKey = firstUnknownKey(o, UP_ENVELOPE_KEYS);
    if (unknownKey !== undefined) {
        return fail('schema_invalid', unknownKey, 'field tak dikenal di amplop naik');
    }
    if (o['envelope_kind'] !== 'up') {
        return fail('schema_invalid', 'envelope_kind', 'envelope_kind wajib "up"');
    }
    const contractVersion = o['contract_version'];
    if (typeof contractVersion !== 'string' || !SEMVER_RE.test(contractVersion)) {
        return fail('schema_invalid', 'contract_version', 'contract_version wajib semver');
    }
    const batchId = syncId(o['batch_id']);
    if (batchId === undefined)
        return fail('schema_invalid', 'batch_id', 'batch_id wajib UUIDv7 (§3)');
    const endpoint = o['endpoint'];
    if (typeof endpoint !== 'string' || !ENDPOINT_RE.test(endpoint)) {
        return fail('schema_invalid', 'endpoint', 'endpoint wajib path ASCII tanpa whitespace');
    }
    const payloadHash = o['payload_hash'];
    if (typeof payloadHash !== 'string' || !PAYLOAD_HASH_RE.test(payloadHash)) {
        return fail('schema_invalid', 'payload_hash', 'payload_hash wajib sha-256 heks huruf kecil (64 char)');
    }
    const outlet = outletCode(o['outlet_code']);
    if (outlet === undefined) {
        return fail('schema_invalid', 'outlet_code', 'outlet_code wajib CODE §2');
    }
    const rawDevice = o['device_code'];
    const device = deviceCode(rawDevice);
    if (device === undefined) {
        const looksIp = typeof rawDevice === 'string' && looksLikeIpAddress(rawDevice);
        return fail('schema_invalid', 'device_code', looksIp
            ? 'device_code berbentuk alamat IP — P10: perangkat dialamati IDENTITAS, bukan IP'
            : 'device_code wajib ada dan berbentuk CODE §2');
    }
    const rawLease = o['lease_token'];
    let leaseToken;
    if (rawLease === null) {
        leaseToken = null;
    }
    else if (isToken(rawLease, LEASE_TOKEN_MAX)) {
        leaseToken = rawLease;
    }
    else {
        return fail('schema_invalid', 'lease_token', 'lease_token wajib string tanpa whitespace atau null');
    }
    const kind = o['kind'];
    if (!isSyncKind(kind))
        return fail('schema_invalid', 'kind', 'kind bukan SyncKind yang dikenal');
    const schemaVersion = o['schema_version'];
    if (!isPositiveInt(schemaVersion)) {
        return fail('schema_invalid', 'schema_version', 'schema_version wajib integer ≥ 1');
    }
    const supported = options?.supported_schema_version;
    if (supported !== undefined && schemaVersion > supported) {
        // I7 — tolak SELURUHNYA. Menelan sebagian tanpa ack per-record = kehilangan senyap.
        return fail('schema_too_new', 'schema_version', `schema_version ${String(schemaVersion)} melampaui yang didukung (${String(supported)})`);
    }
    const rawRecords = o['records'];
    if (!Array.isArray(rawRecords)) {
        return fail('schema_invalid', 'records', 'records wajib array');
    }
    // I3 (P8) diperiksa SEBELUM I2: batch kosong adalah pelanggaran yang lebih dalam daripada
    // salah hitung — jangan terbitkan dokumen bila masukannya tak ada.
    if (rawRecords.length === 0) {
        return fail('empty_batch', 'records', 'batch kosong — P8: jangan terbitkan dokumen tanpa masukan');
    }
    const recordCount = o['record_count'];
    if (!isPositiveInt(recordCount)) {
        return fail('schema_invalid', 'record_count', 'record_count wajib integer ≥ 1');
    }
    if (recordCount !== rawRecords.length) {
        return fail('count_mismatch', 'record_count', `record_count ${String(recordCount)} ≠ records.length ${String(rawRecords.length)}`);
    }
    const records = [];
    const seen = new Set();
    let minId;
    let maxId;
    let minDate;
    let maxDate;
    for (let i = 0; i < rawRecords.length; i += 1) {
        const path = `records[${String(i)}]`;
        const r = asObject(rawRecords[i]);
        if (r === undefined)
            return fail('schema_invalid', path, 'record wajib objek');
        const extra = firstUnknownKey(r, UP_RECORD_KEYS);
        if (extra !== undefined)
            return fail('schema_invalid', `${path}.${extra}`, 'field tak dikenal di record');
        const id = syncId(r['id']);
        if (id === undefined)
            return fail('schema_invalid', `${path}.id`, 'id wajib UUIDv7 (§3)');
        if (seen.has(id)) {
            return fail('duplicate_id_in_batch', `${path}.id`, `id ${id} muncul lebih dari sekali`);
        }
        seen.add(id);
        const revision = r['revision'];
        if (!isPositiveInt(revision)) {
            return fail('schema_invalid', `${path}.revision`, 'revision wajib integer ≥ 1');
        }
        const bdate = businessDate(r['business_date']);
        if (bdate === undefined) {
            return fail('schema_invalid', `${path}.business_date`, 'business_date wajib BDATE §2');
        }
        if (!('payload' in r)) {
            return fail('schema_invalid', `${path}.payload`, 'payload wajib ada');
        }
        const payload = r['payload'];
        // I6 — oportunistik: hanya payload yang MEMBAWA outlet_code bisa berbohong tentang outlet.
        const payloadObj = asObject(payload);
        if (payloadObj !== undefined) {
            const inner = payloadObj['outlet_code'];
            if (typeof inner === 'string' && inner !== outlet) {
                return fail('outlet_mismatch', `${path}.payload.outlet_code`, `record milik outlet ${inner}, header menyatakan ${outlet}`);
            }
        }
        // I8 — P1.
        const violation = scanWirePrecisionViolation(payload, `${path}.payload`);
        if (violation !== undefined) {
            return fail('schema_invalid', violation, 'JSON number hanya sah untuk pencacah yang dikenal (revision/…_count/…_no/…). ' +
                'MONEY/QTY/RATE wajib string desimal kanonik di kawat, tak pernah number (P1)');
        }
        if (minId === undefined || id < minId)
            minId = id;
        if (maxId === undefined || id > maxId)
            maxId = id;
        if (minDate === undefined || bdate < minDate)
            minDate = bdate;
        if (maxDate === undefined || bdate > maxDate)
            maxDate = bdate;
        records.push(Object.freeze({ id, revision, business_date: bdate, payload }));
    }
    // I4 — key_range DIHITUNG, tidak diklaim.
    const keyRange = asObject(o['key_range']);
    if (keyRange === undefined)
        return fail('schema_invalid', 'key_range', 'key_range wajib objek');
    const krFrom = syncId(keyRange['from']);
    const krTo = syncId(keyRange['to']);
    if (krFrom === undefined || krTo === undefined) {
        return fail('schema_invalid', 'key_range', 'key_range.from/to wajib UUIDv7');
    }
    if (firstUnknownKey(keyRange, RANGE_KEYS) !== undefined) {
        return fail('schema_invalid', 'key_range', 'field tak dikenal di key_range');
    }
    if (krFrom !== minId || krTo !== maxId) {
        return fail('schema_invalid', 'key_range', `key_range tak cocok dengan isi (hitungan: ${String(minId)}..${String(maxId)})`);
    }
    const bdRange = asObject(o['business_date_range']);
    if (bdRange === undefined) {
        return fail('schema_invalid', 'business_date_range', 'business_date_range wajib objek');
    }
    const bdFrom = businessDate(bdRange['from']);
    const bdTo = businessDate(bdRange['to']);
    if (bdFrom === undefined || bdTo === undefined) {
        return fail('schema_invalid', 'business_date_range', 'business_date_range.from/to wajib BDATE');
    }
    if (firstUnknownKey(bdRange, RANGE_KEYS) !== undefined) {
        return fail('schema_invalid', 'business_date_range', 'field tak dikenal di business_date_range');
    }
    if (bdFrom !== minDate || bdTo !== maxDate) {
        // SYNC §4: rekonsiliasi wajib di grain TERENDAH. Rentang yang lebih longgar dari isinya
        // menyembunyikan simpangan harian (Juni 2026: hanya 2 dari 30 hari cocok persis).
        return fail('schema_invalid', 'business_date_range', `business_date_range tak cocok dengan isi (hitungan: ${String(minDate)}..${String(maxDate)})`);
    }
    const createdAt = o['created_at'];
    if (!isTimestamp(createdAt)) {
        return fail('schema_invalid', 'created_at', 'created_at wajib TS UTC §2');
    }
    const sentAt = o['sent_at'];
    if (!isTimestamp(sentAt))
        return fail('schema_invalid', 'sent_at', 'sent_at wajib TS UTC §2');
    const attempt = o['attempt'];
    if (!isPositiveInt(attempt)) {
        return fail('schema_invalid', 'attempt', 'attempt wajib integer ≥ 1');
    }
    return ok(Object.freeze({
        contract_version: contractVersion,
        envelope_kind: 'up',
        request_id: requestId,
        batch_id: batchId,
        endpoint,
        payload_hash: payloadHash,
        outlet_code: outlet,
        device_code: device,
        lease_token: leaseToken,
        kind,
        schema_version: schemaVersion,
        record_count: recordCount,
        key_range: Object.freeze({ from: krFrom, to: krTo }),
        business_date_range: Object.freeze({ from: bdFrom, to: bdTo }),
        created_at: createdAt,
        sent_at: sentAt,
        attempt,
        records: Object.freeze(records),
    }));
}
const RANGE_KEYS = new Set(['from', 'to']);
/** Versi **melempar** {@link parseUpBatchEnvelope} untuk jalur internal (bukan batas I/O). */
export function assertUpBatchEnvelope(u, options) {
    return unwrap(parseUpBatchEnvelope(u, options));
}
/* ── Amplop ack ──────────────────────────────────────────────────────────── */
const ACK_ENVELOPE_KEYS = new Set([
    'envelope_kind',
    'contract_version',
    'request_id',
    'batch_id',
    'received_at',
    'processed_at',
    'batch_result',
    'batch_reason',
    'summary',
    'results',
]);
const ACK_RESULT_KEYS = new Set([
    'id',
    'revision',
    'outcome',
    'reason',
    'detail',
]);
const ACK_SUMMARY_KEYS = new Set(['accepted', 'duplicate', 'rejected']);
/**
 * Parser amplop ACK — **tak pernah melempar**.
 *
 * Menegakkan konsistensi yang membuat ack bisa dipercaya:
 * - `reason` wajib cocok dengan `outcome` (P2 — `accepted` ⇒ `null`, `duplicate` ⇒
 *   {@link DuplicateReason}, `rejected` ⇒ {@link RejectReason}).
 * - `summary` wajib **sama persis** dengan tally `results` — ⭐ termasuk `duplicate` yang **tidak
 *   boleh dilebur** ke `accepted` (lihat {@link ACK_OUTCOMES}).
 * - `batch_result` wajib = {@link deriveBatchResult}(`summary`).
 * - `batch_reason` terisi **iff** `batch_result === 'rejected'`.
 */
export function parseAckEnvelope(u) {
    const o = asObject(u);
    if (o === undefined)
        return fail('schema_invalid', '', 'amplop ack wajib objek JSON');
    const unknownKey = firstUnknownKey(o, ACK_ENVELOPE_KEYS);
    if (unknownKey !== undefined)
        return fail('schema_invalid', unknownKey, 'field tak dikenal di ack');
    if (o['envelope_kind'] !== 'ack') {
        return fail('schema_invalid', 'envelope_kind', 'envelope_kind wajib "ack"');
    }
    const contractVersion = o['contract_version'];
    if (typeof contractVersion !== 'string' || !SEMVER_RE.test(contractVersion)) {
        return fail('schema_invalid', 'contract_version', 'contract_version wajib semver');
    }
    const requestId = o['request_id'];
    if (!isToken(requestId, REQUEST_ID_MAX)) {
        return fail('missing_request_id', 'request_id', 'ack wajib meng-echo request_id');
    }
    const batchId = syncId(o['batch_id']);
    if (batchId === undefined)
        return fail('schema_invalid', 'batch_id', 'batch_id wajib UUIDv7');
    const receivedAt = o['received_at'];
    if (!isTimestamp(receivedAt)) {
        return fail('schema_invalid', 'received_at', 'received_at wajib TS UTC (fase 1)');
    }
    const processedAt = o['processed_at'];
    if (processedAt !== null && !isTimestamp(processedAt)) {
        return fail('schema_invalid', 'processed_at', 'processed_at wajib TS UTC atau null (fase 2)');
    }
    const batchResult = o['batch_result'];
    if (!isBatchResult(batchResult)) {
        return fail('schema_invalid', 'batch_result', 'batch_result bukan nilai yang dikenal');
    }
    const batchReason = o['batch_reason'];
    if (batchReason !== null && !isRejectReason(batchReason)) {
        return fail('schema_invalid', 'batch_reason', 'batch_reason wajib RejectReason atau null (P2)');
    }
    if (batchResult === 'rejected' && batchReason === null) {
        return fail('schema_invalid', 'batch_reason', 'batch_result "rejected" wajib menyertakan batch_reason');
    }
    if (batchResult !== 'rejected' && batchReason !== null) {
        return fail('schema_invalid', 'batch_reason', 'batch_reason hanya boleh terisi bila rejected');
    }
    const summaryObj = asObject(o['summary']);
    if (summaryObj === undefined)
        return fail('schema_invalid', 'summary', 'summary wajib objek');
    if (firstUnknownKey(summaryObj, ACK_SUMMARY_KEYS) !== undefined) {
        return fail('schema_invalid', 'summary', 'field tak dikenal di summary');
    }
    const accepted = summaryObj['accepted'];
    const duplicate = summaryObj['duplicate'];
    const rejected = summaryObj['rejected'];
    if (!isCount(accepted) || !isCount(duplicate) || !isCount(rejected)) {
        return fail('schema_invalid', 'summary', 'summary.* wajib integer ≥ 0');
    }
    const rawResults = o['results'];
    if (!Array.isArray(rawResults))
        return fail('schema_invalid', 'results', 'results wajib array');
    const results = [];
    const seen = new Set();
    let tallyAccepted = 0;
    let tallyDuplicate = 0;
    let tallyRejected = 0;
    for (let i = 0; i < rawResults.length; i += 1) {
        const path = `results[${String(i)}]`;
        const r = asObject(rawResults[i]);
        if (r === undefined)
            return fail('schema_invalid', path, 'hasil ack wajib objek');
        const extra = firstUnknownKey(r, ACK_RESULT_KEYS);
        if (extra !== undefined)
            return fail('schema_invalid', `${path}.${extra}`, 'field tak dikenal');
        const id = syncId(r['id']);
        if (id === undefined)
            return fail('schema_invalid', `${path}.id`, 'id wajib UUIDv7');
        if (seen.has(id)) {
            return fail('duplicate_id_in_batch', `${path}.id`, `id ${id} muncul lebih dari sekali di results`);
        }
        seen.add(id);
        const revision = r['revision'];
        if (!isPositiveInt(revision)) {
            return fail('schema_invalid', `${path}.revision`, 'revision wajib integer ≥ 1');
        }
        const outcome = r['outcome'];
        if (!isAckOutcome(outcome)) {
            return fail('schema_invalid', `${path}.outcome`, 'outcome bukan nilai yang dikenal');
        }
        const rawReason = r['reason'];
        let reason;
        if (outcome === 'accepted') {
            if (rawReason !== null) {
                return fail('schema_invalid', `${path}.reason`, 'outcome "accepted" wajib reason null');
            }
            reason = null;
            tallyAccepted += 1;
        }
        else if (outcome === 'duplicate') {
            if (!isDuplicateReason(rawReason)) {
                return fail('schema_invalid', `${path}.reason`, 'outcome "duplicate" wajib DuplicateReason (same_revision | superseded)');
            }
            reason = rawReason;
            tallyDuplicate += 1;
        }
        else {
            if (!isRejectReason(rawReason)) {
                return fail('schema_invalid', `${path}.reason`, 'outcome "rejected" wajib RejectReason (P2)');
            }
            reason = rawReason;
            tallyRejected += 1;
        }
        const rawDetail = r['detail'];
        let detail;
        if (rawDetail === null) {
            detail = null;
        }
        else if (typeof rawDetail === 'string' && rawDetail.length <= DETAIL_MAX) {
            detail = rawDetail;
        }
        else {
            return fail('schema_invalid', `${path}.detail`, 'detail wajib string ≤ 500 char atau null');
        }
        results.push(Object.freeze({ id, revision, outcome, reason, detail }));
    }
    if (accepted !== tallyAccepted || duplicate !== tallyDuplicate || rejected !== tallyRejected) {
        return fail('schema_invalid', 'summary', `summary tak cocok dengan results (tally: ${String(tallyAccepted)}/${String(tallyDuplicate)}/${String(tallyRejected)})`);
    }
    const summary = Object.freeze({ accepted, duplicate, rejected });
    if (deriveBatchResult(summary) !== batchResult) {
        return fail('schema_invalid', 'batch_result', `batch_result "${batchResult}" tak konsisten dengan summary (turunan: "${deriveBatchResult(summary)}")`);
    }
    return ok(Object.freeze({
        envelope_kind: 'ack',
        contract_version: contractVersion,
        request_id: requestId,
        batch_id: batchId,
        received_at: receivedAt,
        processed_at: processedAt,
        batch_result: batchResult,
        batch_reason: batchReason,
        summary,
        results: Object.freeze(results),
    }));
}
function isCount(v) {
    return typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
}
/** Versi **melempar** {@link parseAckEnvelope} untuk jalur internal. */
export function assertAckEnvelope(u) {
    return unwrap(parseAckEnvelope(u));
}
/**
 * Membentuk ack penolakan menyeluruh untuk amplop cacat (I1–I8 gagal).
 *
 * Menjamin bentuk yang diwajibkan §10: `batch_result = 'rejected'`, `results: []`,
 * `batch_reason` terisi, `summary` nol. **Portal tak boleh menelan sebagian dari amplop cacat** —
 * membangun ack ini dengan tangan membuka pintu untuk melanggarnya.
 */
export function buildRejectionAck(params) {
    if (!isRejectReason(params.reason)) {
        throw new SyncContractError('invalid_argument', 'reason wajib RejectReason (P2)', 'reason');
    }
    return Object.freeze({
        envelope_kind: 'ack',
        contract_version: params.contract_version,
        request_id: params.request_id,
        batch_id: params.batch_id,
        received_at: params.received_at,
        processed_at: params.processed_at,
        batch_result: 'rejected',
        batch_reason: params.reason,
        summary: Object.freeze({ accepted: 0, duplicate: 0, rejected: 0 }),
        results: Object.freeze([]),
    });
}
/* ── Pencocokan ack ↔ request ────────────────────────────────────────────── */
/** Kenapa sebuah ack ditolak klien — struktural (P2). */
export const ACK_CHECK_CODES = Object.freeze([
    'request_id_mismatch',
    'batch_id_mismatch',
    'results_incomplete',
    'results_unknown_id',
    'revision_mismatch',
]);
/**
 * Mencocokkan ack dengan amplop yang dikirim.
 *
 * `results` **wajib mencakup setiap `id` dalam request, tepat satu kali**. Ack yang tak lengkap =
 * ack cacat: klien membiarkan sisanya tetap `sent` dan **tidak menebak**. `results` yang memuat
 * `id` di luar request ⇒ ack ditolak.
 *
 * ⚠️ Ack penolakan menyeluruh (`batch_result === 'rejected'` dengan `results: []`) **sah** dan
 * tidak dianggap tak lengkap — di sana nol record memang ditelan.
 */
export function checkAckAgainstRequest(ack, envelope) {
    const violations = [];
    if (ack.request_id !== envelope.request_id) {
        violations.push({
            code: 'request_id_mismatch',
            path: 'request_id',
            detail: `ack meng-echo "${ack.request_id}", request "${envelope.request_id}"`,
        });
    }
    if (ack.batch_id !== envelope.batch_id) {
        violations.push({
            code: 'batch_id_mismatch',
            path: 'batch_id',
            detail: `ack menyebut batch ${ack.batch_id}, request ${envelope.batch_id}`,
        });
    }
    const sent = new Map();
    for (const r of envelope.records)
        sent.set(r.id, r.revision);
    const acked = new Set();
    for (let i = 0; i < ack.results.length; i += 1) {
        const result = ack.results[i];
        if (result === undefined)
            continue;
        const expected = sent.get(result.id);
        if (expected === undefined) {
            violations.push({
                code: 'results_unknown_id',
                path: `results[${String(i)}].id`,
                detail: `${result.id} tidak ada dalam request`,
            });
            continue;
        }
        if (result.revision !== expected) {
            violations.push({
                code: 'revision_mismatch',
                path: `results[${String(i)}].revision`,
                detail: `ack revisi ${String(result.revision)}, dikirim revisi ${String(expected)}`,
            });
        }
        acked.add(result.id);
    }
    const isWholeBatchRejection = ack.batch_result === 'rejected' && ack.results.length === 0;
    if (!isWholeBatchRejection) {
        for (const r of envelope.records) {
            if (!acked.has(r.id)) {
                violations.push({
                    code: 'results_incomplete',
                    path: 'results',
                    detail: `${r.id} tak disebut ack — record tetap "sent", klien tidak menebak`,
                });
            }
        }
    }
    return Object.freeze(violations);
}
/**
 * `id` yang dikirim tapi **tak disebut** ack. Record-record ini **tetap `sent`** — tak ada tebakan,
 * tak ada `applyAck` yang dipanggil untuk mereka.
 */
export function unresolvedIds(ack, envelope) {
    const acked = new Set();
    for (const r of ack.results)
        acked.add(r.id);
    const out = [];
    for (const r of envelope.records) {
        if (!acked.has(r.id))
            out.push(r.id);
    }
    return Object.freeze(out);
}
/* ── Arah turun ──────────────────────────────────────────────────────────── */
const CURSOR_KEYS = new Set(['watermark_ts', 'last_id', 'snapshot_version']);
function parseCursor(u, path) {
    const o = asObject(u);
    if (o === undefined)
        return fail('schema_invalid', path, 'cursor wajib objek');
    const extra = firstUnknownKey(o, CURSOR_KEYS);
    if (extra !== undefined)
        return fail('schema_invalid', `${path}.${extra}`, 'field tak dikenal di cursor');
    const ts = o['watermark_ts'];
    if (!isTimestamp(ts))
        return fail('schema_invalid', `${path}.watermark_ts`, 'watermark_ts wajib TS UTC');
    const lastId = syncId(o['last_id']);
    if (lastId === undefined)
        return fail('schema_invalid', `${path}.last_id`, 'last_id wajib UUIDv7');
    const snapshotVersion = o['snapshot_version'];
    if (!isPositiveInt(snapshotVersion)) {
        return fail('schema_invalid', `${path}.snapshot_version`, 'snapshot_version wajib integer ≥ 1');
    }
    return ok(Object.freeze({ watermark_ts: ts, last_id: lastId, snapshot_version: snapshotVersion }));
}
const DOWN_PULL_KEYS = new Set([
    'contract_version',
    'outlet_code',
    'device_code',
    'kind',
    'cursor',
    'max_records',
]);
/**
 * Parser permintaan tarik master — **tak pernah melempar**.
 *
 * Perhatikan yang **tidak** ada: `request_id` dan `sync_state`. Idempotensi arah turun bersifat
 * **alami** (upsert by `id`); kiriman ganda tak berbahaya, yang terlewat terkejar kursor berikutnya.
 */
export function parseDownPullRequest(u) {
    const o = asObject(u);
    if (o === undefined)
        return fail('schema_invalid', '', 'permintaan tarik wajib objek JSON');
    const extra = firstUnknownKey(o, DOWN_PULL_KEYS);
    if (extra !== undefined)
        return fail('schema_invalid', extra, 'field tak dikenal di permintaan tarik');
    const contractVersion = o['contract_version'];
    if (typeof contractVersion !== 'string' || !SEMVER_RE.test(contractVersion)) {
        return fail('schema_invalid', 'contract_version', 'contract_version wajib semver');
    }
    const outlet = outletCode(o['outlet_code']);
    if (outlet === undefined)
        return fail('schema_invalid', 'outlet_code', 'outlet_code wajib CODE §2');
    const rawDevice = o['device_code'];
    const device = deviceCode(rawDevice);
    if (device === undefined) {
        const looksIp = typeof rawDevice === 'string' && looksLikeIpAddress(rawDevice);
        return fail('schema_invalid', 'device_code', looksIp ? 'device_code berbentuk alamat IP — P10' : 'device_code wajib CODE §2');
    }
    const kind = o['kind'];
    if (!isMasterKind(kind))
        return fail('schema_invalid', 'kind', 'kind bukan MasterKind yang dikenal');
    const rawCursor = o['cursor'];
    let cursor = null;
    if (rawCursor !== null) {
        const parsed = parseCursor(rawCursor, 'cursor');
        if (!parsed.ok)
            return parsed;
        cursor = parsed.value;
    }
    const maxRecords = o['max_records'];
    if (!isPositiveInt(maxRecords)) {
        return fail('schema_invalid', 'max_records', 'max_records wajib integer ≥ 1');
    }
    return ok(Object.freeze({
        contract_version: contractVersion,
        outlet_code: outlet,
        device_code: device,
        kind,
        cursor,
        max_records: maxRecords,
    }));
}
/** Versi **melempar** {@link parseDownPullRequest}. */
export function assertDownPullRequest(u) {
    return unwrap(parseDownPullRequest(u));
}
const DOWN_ENVELOPE_KEYS = new Set([
    'envelope_kind',
    'contract_version',
    'kind',
    'mode',
    'snapshot_version',
    'generated_at',
    'records',
    'deletes',
    'next_cursor',
    'has_more',
]);
const DOWN_RECORD_KEYS = new Set(['id', 'origin', 'payload']);
/**
 * Parser amplop master TURUN — **tak pernah melempar**.
 *
 * Aturan yang ditegakkan:
 * - `mode: 'delta'` **wajib** memuat `deletes` (boleh kosong) — delta tanpa penghapusan berarti menu
 *   yang dihentikan tetap bisa dijual di outlet. Pada `snapshot`, isinya diabaikan.
 * - `has_more: true` ⇒ `next_cursor` **non-null**; `has_more: false` + `next_cursor: null` = mutakhir.
 * - `origin` wajib ada (`central` | `branch`) — pengganti struktural `AddingFromBranch` VTEC.
 * - Satu `id` tak boleh muncul di `records` **dan** `deletes` sekaligus: instruksi yang saling
 *   bertentangan harus berisik, bukan diselesaikan dengan menebak urutan.
 */
export function parseDownEnvelope(u) {
    const o = asObject(u);
    if (o === undefined)
        return fail('schema_invalid', '', 'amplop turun wajib objek JSON');
    const extra = firstUnknownKey(o, DOWN_ENVELOPE_KEYS);
    if (extra !== undefined)
        return fail('schema_invalid', extra, 'field tak dikenal di amplop turun');
    if (o['envelope_kind'] !== 'down') {
        return fail('schema_invalid', 'envelope_kind', 'envelope_kind wajib "down"');
    }
    const contractVersion = o['contract_version'];
    if (typeof contractVersion !== 'string' || !SEMVER_RE.test(contractVersion)) {
        return fail('schema_invalid', 'contract_version', 'contract_version wajib semver');
    }
    const kind = o['kind'];
    if (!isMasterKind(kind))
        return fail('schema_invalid', 'kind', 'kind bukan MasterKind yang dikenal');
    const mode = o['mode'];
    if (!isDownMode(mode))
        return fail('schema_invalid', 'mode', 'mode wajib "snapshot" atau "delta"');
    const snapshotVersion = o['snapshot_version'];
    if (!isPositiveInt(snapshotVersion)) {
        return fail('schema_invalid', 'snapshot_version', 'snapshot_version wajib integer ≥ 1');
    }
    const generatedAt = o['generated_at'];
    if (!isTimestamp(generatedAt)) {
        return fail('schema_invalid', 'generated_at', 'generated_at wajib TS UTC');
    }
    const rawRecords = o['records'];
    if (!Array.isArray(rawRecords))
        return fail('schema_invalid', 'records', 'records wajib array');
    const records = [];
    const ids = new Set();
    for (let i = 0; i < rawRecords.length; i += 1) {
        const path = `records[${String(i)}]`;
        const r = asObject(rawRecords[i]);
        if (r === undefined)
            return fail('schema_invalid', path, 'record wajib objek');
        const bad = firstUnknownKey(r, DOWN_RECORD_KEYS);
        if (bad !== undefined)
            return fail('schema_invalid', `${path}.${bad}`, 'field tak dikenal');
        const id = syncId(r['id']);
        if (id === undefined)
            return fail('schema_invalid', `${path}.id`, 'id wajib UUIDv7');
        if (ids.has(id)) {
            return fail('duplicate_id_in_batch', `${path}.id`, `id ${id} muncul lebih dari sekali`);
        }
        ids.add(id);
        const origin = r['origin'];
        if (!isDownOrigin(origin)) {
            return fail('schema_invalid', `${path}.origin`, 'origin wajib "central" atau "branch"');
        }
        if (!('payload' in r))
            return fail('schema_invalid', `${path}.payload`, 'payload wajib ada');
        const payload = r['payload'];
        const violation = scanWirePrecisionViolation(payload, `${path}.payload`);
        if (violation !== undefined) {
            return fail('schema_invalid', violation, 'JSON number hanya sah untuk pencacah yang dikenal (revision/…_count/…_no/…). ' +
                'MONEY/QTY/RATE wajib string desimal kanonik di kawat, tak pernah number (P1)');
        }
        records.push(Object.freeze({ id, origin, payload }));
    }
    const rawDeletes = o['deletes'];
    if (!Array.isArray(rawDeletes)) {
        return fail('schema_invalid', 'deletes', 'deletes wajib array (boleh kosong) — delta tanpa penghapusan membuat menu yang dihentikan tetap terjual');
    }
    const deletes = [];
    for (let i = 0; i < rawDeletes.length; i += 1) {
        const id = syncId(rawDeletes[i]);
        if (id === undefined) {
            return fail('schema_invalid', `deletes[${String(i)}]`, 'deletes[] wajib UUIDv7');
        }
        if (mode === 'delta' && ids.has(id)) {
            return fail('schema_invalid', `deletes[${String(i)}]`, `id ${id} muncul di records DAN deletes — instruksi bertentangan`);
        }
        deletes.push(id);
    }
    const hasMore = o['has_more'];
    if (typeof hasMore !== 'boolean') {
        return fail('schema_invalid', 'has_more', 'has_more wajib boolean');
    }
    const rawNext = o['next_cursor'];
    let nextCursor = null;
    if (rawNext !== null) {
        const parsed = parseCursor(rawNext, 'next_cursor');
        if (!parsed.ok)
            return parsed;
        nextCursor = parsed.value;
    }
    if (hasMore && nextCursor === null) {
        return fail('schema_invalid', 'next_cursor', 'has_more:true wajib disertai next_cursor');
    }
    return ok(Object.freeze({
        envelope_kind: 'down',
        contract_version: contractVersion,
        kind,
        mode,
        snapshot_version: snapshotVersion,
        generated_at: generatedAt,
        records: Object.freeze(records),
        deletes: Object.freeze(deletes),
        next_cursor: nextCursor,
        has_more: hasMore,
    }));
}
/** Versi **melempar** {@link parseDownEnvelope}. */
export function assertDownEnvelope(u) {
    return unwrap(parseDownEnvelope(u));
}
//# sourceMappingURL=sync.js.map